import React, { useState, useEffect } from 'react';
import Axios from 'axios';

/* Components */
import CardProduct from 'js/components/molecules/CardProduct';
import CardProductSkeleton from 'js/components/molecules/CardProductSkeleton';

/* Utilities */
import { trans, getImageFraction } from 'js/utilities';
import { getShopData } from 'js/utilities/shopStorage';

export default function RecommendedAccessories({ productId, shopwareUrl, relatedProductsUrl, contentBlockProductsUrl, type = 'modal_recommended_products' }) {
    const [recommendations, setRecommendations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadRecommendations = async () => {
            if (!shopwareUrl) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                const vehicleFilters = getShopData('vehicleFilters', null);
                const hasVehicleData = vehicleFilters && vehicleFilters.brand && vehicleFilters.model;

                let url, params;

                if (hasVehicleData && relatedProductsUrl) {
                    // Use related products endpoint with vehicle data
                    url = relatedProductsUrl;
                    params = {
                        brand: vehicleFilters.brand,
                        model: vehicleFilters.model,
                        ...(vehicleFilters.constructionYear ? {constructionYear: vehicleFilters.constructionYear} : {}),
                        ...(vehicleFilters.height ? {height: vehicleFilters.height} : {}),
                        ...(vehicleFilters.length ? {length: vehicleFilters.length} : {}),
                        ...(vehicleFilters.vehicleType ? {vehicleType: vehicleFilters.vehicleType} : {}),
                        ...(type ? {type: type} : {}),
                        ...(productId ? {excludeProductId: productId} : {}),
                        limit: 3
                    };
                } else if (contentBlockProductsUrl) {
                    // Use content block products endpoint as fallback
                    url = contentBlockProductsUrl;
                    params = {
                        ...(type ? {type: type} : {}),
                        ...(productId ? {excludeProductId: productId} : {}),
                        limit: 3
                    };
                } else {
                    // No valid URL available
                    setRecommendations([]);
                    setIsLoading(false);
                    return;
                }

                const response = await Axios.get(url, {params});

                if (response.data && response.data.items) {
                    setRecommendations(response.data.items);
                } else {
                    setRecommendations([]);
                }
            } catch (err) {
                setError('Er is iets misgegaan bij het laden van aanbevelingen');
                setRecommendations([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadRecommendations();
    }, []);

    if (isLoading) {
        return (
            <div className="py-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-3">{trans('sulu.content_block.product_overview.modal.recommendations.title')}</h5>
                </div>

                <div className="row gutters-2">
                    {[...Array(3)].map((_, index) => (
                        <div key={`skeleton-${index}`} className="col-12 col-md-4">
                            <CardProductSkeleton smallView={true}/>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error || !recommendations.length) {
        return (
            <div className="py-4">
                <h5 className="mb-3">{trans('sulu.content_block.product_overview.modal.recommendations.title')}</h5>
                <p className="text-muted text--14">
                    {error || trans('sulu.content_block.product_overview.modal.recommendations.not_found')}
                </p>
            </div>
        );
    }

    return (
        <div className="py-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">{trans('sulu.content_block.product_overview.modal.recommendations.title')}</h5>
            </div>

            <div className="row gutters-2">
                {recommendations.map((product, index) => {
                    return (
                        <div key={product.id || index} className="col-12 col-md-4">
                            <CardProduct
                                product={{
                                    id: product.id,
                                    name: product.label || '',
                                    productNumber: product.number || '',
                                    price: product.price || 0,
                                    manufacturer: product.manufacturer || '',
                                    image: product.image || '',
                                }}
                                image={{
                                    alt: product.label || '',
                                    width: 1920,
                                    height: 1440,
                                    ...getImageFraction({
                                        path: product.image || '',
                                        ratio: 3 / 2,
                                        fraction: 1,
                                        breakpoints: {
                                            md: {fraction: 1 / 2},
                                            xl: {fraction: 1 / 3},
                                            xxl: {fraction: 1 / 4}
                                        }
                                    }),
                                    ...((index >= 1) ? {loading: 'lazy'} : null),
                                }}
                                shopwareUrl={shopwareUrl}
                                relatedProductsUrl={relatedProductsUrl}
                                contentBlockProductsUrl={contentBlockProductsUrl}
                                disableModal={true}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
